import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Package, Heart, Star, Bell, Wallet, Copy, Download, Loader2, AlertTriangle, ShieldCheck, Hand, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, fmtDate, fmtDateTime, mediaSrc, BACKEND, DELIVERY_LABEL } from "@/lib/api";
import { PageTitle, StatCard, EmptyState, StatusBadge, ProductGrid, Stars, Field } from "@/components/vaulted";
import { DiscordButton } from "@/pages/Auth";

const useFetch = (url, deps = []) => {
  const [d, setD] = useState(null);
  const load = () => api.get(url).then((r) => setD(r.data)).catch(() => setD([]));
  useEffect(() => { load(); }, deps); // eslint-disable-line
  return [d, load];
};

function VerifyBanner() {
  const { user } = useAuth();
  if (!user || user.email_verified) return null;
  return (
    <div data-testid="verify-email-banner" className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
      <AlertTriangle className="h-4 w-4" />Please verify your email address ({user.email}).
      <button className="ml-auto v-btn-ghost v-btn-sm" onClick={() => api.post("/auth/resend-verification").then(() => toast.success("Verification email sent")).catch((e) => toast.error(errMsg(e)))}>Resend email</button>
    </div>
  );
}

const OrdersTable = ({ orders, testId }) => (
  <div className="v-panel overflow-x-auto" data-testid={testId}>
    <table className="v-table w-full min-w-[720px]">
      <thead><tr><th>Order</th><th>Product</th><th>Seller</th><th>Total</th><th>Date</th><th>Payment</th><th>Delivery</th></tr></thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id} data-testid={`order-row-${o.number}`}>
            <td><Link to={`/account/orders/${o.id}`} className="font-mono text-xs text-violet-300 hover:text-violet-200">{o.number}</Link>{o.is_test && <span className="ml-1.5 text-[10px] text-amber-300">TEST</span>}</td>
            <td className="max-w-[220px] truncate text-white">{o.items[0]?.title}{o.items.length > 1 && <span className="text-zinc-500"> +{o.items.length - 1}</span>}</td>
            <td className="text-zinc-400">{[...new Set(o.items.map((i) => i.seller_name))].join(", ")}</td>
            <td className="text-white">{money(o.total)}</td>
            <td className="text-zinc-400">{fmtDate(o.created_at)}</td>
            <td><StatusBadge status={o.status} /></td>
            <td><StatusBadge status={o.items.every((i) => ["delivered", "fulfilled"].includes(i.fulfilment_status)) ? "delivered" : o.items.find((i) => !["delivered", "fulfilled"].includes(i.fulfilment_status))?.fulfilment_status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function AccountOverview() {
  const { user } = useAuth();
  const [d] = useFetch("/account/overview");
  return (
    <div data-testid="account-overview">
      <VerifyBanner />
      <PageTitle title={`Welcome back, ${user.username}`} subtitle="Your purchases, saved items and account activity." />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Orders" value={d?.orders ?? "—"} icon={Package} testId="stat-orders" />
        <StatCard label="Total spent" value={d ? money(d.total_spent) : "—"} icon={Wallet} testId="stat-spent" />
        <StatCard label="Wishlist" value={d?.wishlist ?? "—"} icon={Heart} testId="stat-wishlist" />
        <StatCard label="Unread notifications" value={d?.unread_notifications ?? "—"} icon={Bell} testId="stat-notifications" />
      </div>
      <h2 className="mb-3 mt-8 text-sm font-semibold text-white">Recent orders</h2>
      {d?.recent_orders?.length ? <OrdersTable orders={d.recent_orders} testId="recent-orders-table" /> :
        <EmptyState icon={Package} title="No orders yet" action={<Link to="/marketplace" className="v-btn-primary v-btn-sm">Browse marketplace</Link>} />}
    </div>
  );
}

export function AccountOrders() {
  const [orders] = useFetch("/orders");
  return (
    <div data-testid="account-orders">
      <PageTitle title="My Orders" subtitle="Order history, payment and delivery status." />
      {orders === null ? <Loader2 className="h-5 w-5 animate-spin text-violet-400" /> : orders.length ? <OrdersTable orders={orders} testId="orders-table" /> :
        <EmptyState icon={Package} title="No orders yet" testId="orders-empty" action={<Link to="/marketplace" className="v-btn-primary v-btn-sm">Browse marketplace</Link>} />}
    </div>
  );
}

function ReviewForm({ item, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try { await api.post("/reviews", { product_id: item.product_id, rating, comment }); toast.success("Review posted"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 rounded-md border border-[#27272A] bg-[#0d0d10] p-3" data-testid={`review-form-${item.product_id}`}>
      <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <button key={i} onClick={() => setRating(i)} data-testid={`review-star-${i}`}><Star className={`h-5 w-5 ${i <= rating ? "fill-violet-400 text-violet-400" : "text-zinc-700"}`} /></button>)}</div>
      <textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Share your experience…" className="v-input mt-2 h-auto py-2" />
      <button data-testid="review-submit-button" onClick={submit} disabled={comment.trim().length < 3} className="mt-2 v-btn-primary v-btn-sm">Post review</button>
    </div>
  );
}

function DeliveryBlock({ order, item, reload }) {
  const [reviewing, setReviewing] = useState(false);
  const paid = ["PAID", "PROCESSING", "COMPLETED"].includes(order.status);
  const download = async () => {
    try { const { data } = await api.post(`/orders/${order.id}/items/${item.id}/download-link`); window.location.href = `${BACKEND}${data.url}`; }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="v-card p-4" data-testid={`order-item-${item.id}`}>
      <div className="flex gap-4">
        <div className="aspect-video w-28 shrink-0 overflow-hidden rounded-md border border-[#27272A] bg-[#111116]">{item.thumbnail && <img src={mediaSrc(item.thumbnail)} alt="" className="h-full w-full object-cover" />}</div>
        <div className="min-w-0 flex-1">
          <Link to={`/product/${item.product_id}`} className="text-sm font-medium text-white hover:text-violet-200">{item.title}</Link>
          <p className="text-xs text-zinc-500">{item.seller_name} · {DELIVERY_LABEL[item.delivery_method]} · Qty {item.quantity}</p>
          <div className="mt-1.5"><StatusBadge status={item.fulfilment_status} testId={`item-status-${item.id}`} /></div>
        </div>
        <p className="text-sm font-semibold text-white">{money(item.line_total)}</p>
      </div>
      {paid && item.delivery_method === "licence_key" && item.licence_keys?.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`licence-keys-${item.id}`}>
          {item.licence_keys.map((k) => (
            <div key={k} className="flex items-center gap-2 rounded-md border border-violet-500/20 bg-violet-500/5 px-3 py-2">
              <code data-testid="licence-key-value" title="Account credentials" className="flex-1 font-mono text-sm text-violet-100 break-all">{k}</code>
              <button onClick={() => { navigator.clipboard.writeText(k); toast.success("Copied"); }} className="text-zinc-400 hover:text-white" data-testid="copy-licence-key"><Copy className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {paid && item.delivery_method === "download" && (
        <button onClick={download} data-testid={`download-button-${item.id}`} className="mt-4 v-btn-primary v-btn-sm"><Download className="h-3.5 w-3.5" />Download file</button>
      )}
      {paid && item.delivery_method === "discord_ticket" && (
        <div className="mt-4 rounded-md border border-[#5865F2]/40 bg-[#5865F2]/10 p-4" data-testid={`ticket-claim-${item.id}`}>
          {["delivered", "fulfilled"].includes(item.fulfilment_status) ? <p className="text-sm text-zinc-200 whitespace-pre-line">{item.delivery_note || "Delivered in your Discord ticket."}</p> : <>
            <p className="text-sm font-medium text-white">Claim your account in our Discord</p>
            <p className="mt-1 text-xs text-zinc-400">Open a ticket and paste your order number and claim code.</p>
            <div className="mt-3 flex flex-wrap gap-2 font-mono text-xs">
              <button onClick={() => { navigator.clipboard.writeText(order.number); toast.success("Copied"); }} className="rounded border border-[#27272A] bg-black/30 px-2.5 py-1.5 text-zinc-200" data-testid="copy-order-number">Order {order.number} <Copy className="ml-1 inline h-3 w-3" /></button>
              <button onClick={() => { navigator.clipboard.writeText(item.claim_code); toast.success("Copied"); }} className="rounded border border-[#27272A] bg-black/30 px-2.5 py-1.5 text-violet-200" data-testid="copy-claim-code">Code {item.claim_code} <Copy className="ml-1 inline h-3 w-3" /></button>
            </div>
            {item.discord_url && <a href={item.discord_url} target="_blank" rel="noopener noreferrer" data-testid="claim-in-discord" className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#5865F2] px-4 py-2 text-sm font-medium text-white hover:bg-[#4752c4]">Claim in Discord</a>}
          </>}
        </div>
      )}
      {paid && item.instructions && (
        <div className="mt-3 rounded-md border border-[#27272A] bg-[#0d0d10] p-3" data-testid={`instructions-${item.id}`}>
          <p className="text-xs text-zinc-400">Instructions</p><p className="mt-1 text-sm text-zinc-200 whitespace-pre-line">{item.instructions}</p>
        </div>
      )}
      {item.delivery_method === "manual" && (
        <div className="mt-4 rounded-md border border-[#27272A] bg-[#0d0d10] p-3 text-sm" data-testid={`manual-delivery-${item.id}`}>
          <p className="flex items-center gap-1.5 text-xs text-zinc-400"><Hand className="h-3.5 w-3.5" />Manual delivery by seller</p>
          <p className="mt-1 text-zinc-200 whitespace-pre-line">{item.delivery_note || "The seller has been notified and will fulfil your order."}</p>
        </div>
      )}
      {order.status === "COMPLETED" && !item.reviewed && (reviewing ? <ReviewForm item={item} onDone={reload} /> :
        <button data-testid={`write-review-${item.id}`} onClick={() => setReviewing(true)} className="mt-3 text-xs text-violet-300 hover:text-violet-200">Write a review</button>)}
      {item.reviewed && <p className="mt-3 flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" />Reviewed</p>}
    </div>
  );
}

export function AccountOrderDetail() {
  const { id } = useParams();
  const [o, load] = useFetch(`/orders/${id}`, [id]);
  const [dispute, setDispute] = useState(false);
  const [reason, setReason] = useState("");
  if (!o || Array.isArray(o)) return o ? <EmptyState title="Order not found" /> : <Loader2 className="h-5 w-5 animate-spin text-violet-400" />;
  const openDispute = async () => {
    try { await api.post(`/orders/${o.id}/dispute`, { reason }); toast.success("Dispute opened"); setDispute(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="order-detail">
      <PageTitle title={<span className="font-mono">{o.number}</span>} subtitle={`Placed ${fmtDateTime(o.created_at)}`}
        action={<div className="flex items-center gap-2">{o.is_test && <span className="rounded border border-amber-500/30 px-1.5 py-0.5 text-[10px] text-amber-300">TEST ORDER</span>}<StatusBadge status={o.status} testId="order-status" /></div>} />
      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="space-y-3">{o.items.map((i) => <DeliveryBlock key={i.id} order={o} item={i} reload={load} />)}</div>
        <aside className="v-panel h-fit p-5 text-sm">
          <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
          {(o.discounts_applied || []).map((d) => <div key={d.id} className="mt-2 flex justify-between text-emerald-400"><span>{d.code || d.name}</span><span>−{money(d.amount)}</span></div>)}
          <div className="mt-3 flex justify-between border-t border-[#27272A] pt-3 font-semibold text-white"><span>Total</span><span>{money(o.total)}</span></div>
          <p className="mt-3 text-xs text-zinc-500">Paid via {o.payment?.provider === "test" ? "development test checkout" : "Stripe"}{o.paid_at ? ` · ${fmtDateTime(o.paid_at)}` : ""}</p>
          {o.dispute && <div className="mt-4 rounded-md border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-200">Dispute {o.dispute.status}{o.dispute.resolution ? ` · ${o.dispute.resolution}` : ""}</div>}
          {["PAID", "PROCESSING", "COMPLETED"].includes(o.status) && <button data-testid="open-dispute-button" onClick={() => setDispute(true)} className="mt-4 v-btn-ghost v-btn-sm w-full">Report a problem</button>}
        </aside>
      </div>
      <Dialog open={dispute} onOpenChange={setDispute}>
        <DialogContent className="bg-[#111116] border-[#27272A]">
          <DialogHeader><DialogTitle>Open a dispute</DialogTitle></DialogHeader>
          <Field label="What went wrong?"><textarea data-testid="dispute-reason-input" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} className="v-input h-auto py-2" /></Field>
          <button data-testid="dispute-submit-button" disabled={reason.length < 10} onClick={openDispute} className="v-btn-primary">Submit dispute</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AccountWishlist() {
  const [items] = useFetch("/account/wishlist");
  return (
    <div data-testid="account-wishlist">
      <PageTitle title="Wishlist" subtitle="Products you've saved for later." />
      {items === null ? <Loader2 className="h-5 w-5 animate-spin text-violet-400" /> : items.length ? <ProductGrid items={items} testId="wishlist-grid" /> : <EmptyState icon={Heart} title="Your wishlist is empty" testId="wishlist-empty" />}
    </div>
  );
}

export function AccountReviews() {
  const [items] = useFetch("/account/reviews");
  return (
    <div data-testid="account-reviews">
      <PageTitle title="My Reviews" subtitle="Reviews you've written for completed purchases." />
      {items?.length ? <div className="space-y-3">{items.map((r) => (
        <div key={r.id} className="v-card p-4"><div className="flex items-center gap-2"><Link to={`/product/${r.product_id}`} className="text-sm font-medium text-white hover:text-violet-200">{r.product_title}</Link><Stars value={r.rating} className="ml-auto" /></div>
          <p className="mt-2 text-sm text-zinc-300">{r.comment}</p><p className="mt-2 text-[11px] text-zinc-600">{fmtDate(r.created_at)} {r.status === "removed" && "· removed by staff"}</p></div>
      ))}</div> : <EmptyState icon={Star} title="No reviews yet" body="You can review products from completed orders." />}
    </div>
  );
}

export function AccountNotifications() {
  const [items, load] = useFetch("/account/notifications");
  return (
    <div data-testid="account-notifications">
      <PageTitle title="Notifications" action={<button data-testid="mark-all-read" onClick={() => api.post("/account/notifications/read-all").then(load)} className="v-btn-ghost v-btn-sm">Mark all read</button>} />
      {items?.length ? <div className="v-panel divide-y divide-[#27272A]">{items.map((n) => (
        <div key={n.id} className="flex gap-3 p-4" data-testid={`notification-${n.id}`}>
          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-zinc-700" : "bg-violet-500"}`} />
          <div className="min-w-0 flex-1"><p className="text-sm text-white">{n.link ? <Link to={n.link} className="hover:text-violet-200">{n.title}</Link> : n.title}</p>{n.body && <p className="text-sm text-zinc-400">{n.body}</p>}</div>
          <span className="text-[11px] text-zinc-600">{fmtDateTime(n.created_at)}</span>
        </div>
      ))}</div> : <EmptyState icon={Bell} title="No notifications" />}
    </div>
  );
}

export function AccountSettings() {
  const { user, setUser } = useAuth();
  const [username, setUsername] = useState(user.username);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [sessions, loadSessions] = useFetch("/account/sessions");
  const saveProfile = async () => {
    try { const { data } = await api.patch("/account/profile", { username }); setUser(data); toast.success("Profile updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const savePw = async () => {
    try { await api.post("/account/password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Password updated"); setUser({ ...user, has_password: true }); loadSessions(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="account-settings" className="space-y-6">
      <PageTitle title="Account Settings" />
      <div className="v-panel p-5 space-y-4 max-w-xl">
        <p className="text-sm font-semibold text-white">Profile</p>
        <Field label="Username"><input data-testid="settings-username-input" value={username} onChange={(e) => setUsername(e.target.value)} className="v-input" /></Field>
        <Field label="Email" hint={user.email_verified ? "Verified" : "Not verified"}><input disabled value={user.email} className="v-input opacity-60" /></Field>
        <button data-testid="settings-save-profile" onClick={saveProfile} className="v-btn-primary v-btn-sm">Save profile</button>
      </div>
      <div className="v-panel p-5 space-y-4 max-w-xl">
        <p className="text-sm font-semibold text-white">{user.has_password ? "Change password" : "Set a password"}</p>
        {user.has_password && <Field label="Current password"><input data-testid="settings-current-password" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="v-input" /></Field>}
        <Field label="New password"><input data-testid="settings-new-password" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="v-input" /></Field>
        <button data-testid="settings-save-password" onClick={savePw} className="v-btn-primary v-btn-sm">Update password</button>
      </div>
      <div className="v-panel p-5 max-w-xl">
        <div className="flex items-center justify-between"><p className="text-sm font-semibold text-white">Active sessions</p>
          <button data-testid="logout-all-button" onClick={() => api.post("/auth/logout-all").then(() => { window.location.href = "/login"; })} className="v-btn-ghost v-btn-sm">Log out everywhere</button></div>
        <div className="mt-3 divide-y divide-[#27272A]">{(sessions || []).map((s) => (
          <div key={s.id} className="py-2.5 text-xs"><p className="truncate text-zinc-300">{s.user_agent || "Unknown device"}</p><p className="text-zinc-500">{s.ip} · {fmtDateTime(s.created_at)} {s.current && <span className="text-emerald-400">· this device</span>}</p></div>
        ))}</div>
      </div>
    </div>
  );
}

export function AccountLinked() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/auth/discord/status").then((r) => setStatus(r.data)); }, []);
  useEffect(() => {
    if (params.get("linked")) toast.success("Discord account linked");
    if (params.get("error") === "discord_already_linked") toast.error("That Discord account is linked to another Vaulted account");
  }, []); // eslint-disable-line
  const unlink = async () => { try { await api.post("/auth/discord/unlink"); await refresh(); toast.success("Discord unlinked"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="account-linked">
      <PageTitle title="Linked Accounts" subtitle="Connect Discord for one-click login and future role sync and purchase notifications." />
      <div className="v-panel max-w-xl p-5">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-[#5865F2]/15 text-[#8891f7] font-semibold">D</div>
          <div className="flex-1"><p className="text-sm font-medium text-white">Discord</p>
            <p className="text-xs text-zinc-500" data-testid="discord-link-status">{user.discord_id ? `Linked as ${user.discord_username} (ID ${user.discord_id})` : "Not linked"}</p></div>
          {user.discord_id && <button data-testid="discord-unlink-button" onClick={unlink} className="v-btn-ghost v-btn-sm">Unlink</button>}
        </div>
        {!user.discord_id && <div className="mt-4">{status?.configured ? <DiscordButton label="Link Discord account" mode="link" testId="discord-link-button" /> :
          <p data-testid="discord-not-configured" className="text-xs text-amber-300/90">Discord OAuth isn't configured yet. An administrator needs to add the Discord Client ID and Secret.</p>}</div>}
        <p className="mt-4 flex items-center gap-1.5 text-[11px] text-zinc-500"><ShieldCheck className="h-3.5 w-3.5" />We only request your Discord username, ID and email.</p>
      </div>
    </div>
  );
}
